import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { auth } from '../../firebase/config';
import {
  getAdminPermissions,
  getEmailVerificationSummary,
  getRevenueFunnelReport,
} from '../../api/adminService';
import './RevenueFunnelPage.css';


const TABS = [
  { key: 'overview', label: '핵심 진단' },
  { key: 'funnel', label: '유입·전환' },
  { key: 'product', label: '제품 가치' },
  { key: 'revenue', label: '결제·매출' },
  { key: 'retention', label: '리텐션·갱신' },
  { key: 'quality', label: '데이터 품질' },
];

const FUNNEL_LABELS = {
  pro_modal_open: 'PRO 관심',
  free_trial_start: '무료체험 시작',
  payment_page_view: '결제 페이지 조회',
  payment_start: '토스 결제 시작',
  payment_ground_truth: '결제 원장 완료',
};

const fmtNum = (value) => (
  value == null ? '—' : Math.round(value).toLocaleString('ko-KR')
);

const fmtWon = (value) => (
  value == null ? '—' : `${Math.round(value).toLocaleString('ko-KR')}원`
);

const fmtPct = (value) => (
  value == null ? '—' : `${Number(value).toFixed(2)}%`
);

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ko-KR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const reportSourceLabel = (serviceMeta) => {
  if (serviceMeta?.cache_hit) return '프로세스 캐시';
  if (serviceMeta?.data_source === 'firestore_snapshot') return '공유 스냅샷';
  return '새로 계산';
};

const KpiCard = ({
  grade,
  label,
  note,
  value,
}) => (
  <article className="revenue-funnel-kpi">
    <div className="revenue-funnel-kpi-head">
      <span>{label}</span>
      <span className={`revenue-funnel-grade grade-${grade.toLowerCase()}`}>
        근거 {grade}
      </span>
    </div>
    <strong>{value}</strong>
    <p>{note}</p>
  </article>
);

const SectionHeader = ({ description, title }) => (
  <header className="revenue-funnel-section-head">
    <h2>{title}</h2>
    {description && <p>{description}</p>}
  </header>
);

const AccessState = ({
  actionLabel = '관리자 홈',
  description,
  onAction,
  title,
}) => (
  <main className="revenue-funnel-access">
    <h1>{title}</h1>
    <p>{description}</p>
    <button type="button" onClick={onAction}>{actionLabel}</button>
  </main>
);

const PageSkeleton = () => (
  <div className="revenue-funnel-page" aria-busy="true">
    <main className="revenue-funnel-container">
      <div className="revenue-funnel-skeleton skeleton-title" />
      <div className="revenue-funnel-skeleton-grid">
        {[0, 1, 2, 3].map((item) => (
          <div
            className="revenue-funnel-skeleton skeleton-card"
            key={item}
          />
        ))}
      </div>
      <div className="revenue-funnel-skeleton skeleton-panel" />
      <div className="revenue-funnel-skeleton skeleton-panel" />
    </main>
  </div>
);

const RevenueTrendChart = ({ rows }) => {
  if (!rows?.length) {
    return <p className="revenue-funnel-empty">매출 추이 데이터가 없어요.</p>;
  }
  const chartRows = rows.slice(-12);
  return (
    <div
      className="revenue-funnel-chart"
      aria-label="월별 확인 매출과 외부계약 추정액 차트"
    >
      <ResponsiveContainer width="100%" height={320}>
        <BarChart
          data={chartRows}
          margin={{ top: 12, right: 20, left: 12, bottom: 8 }}
        >
          <CartesianGrid stroke="#f2f4f6" vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fill: '#6b7684', fontSize: 12 }}
          />
          <YAxis
            tick={{ fill: '#6b7684', fontSize: 12 }}
            tickFormatter={(value) => `${Math.round(value / 10000)}만`}
            width={66}
          />
          <Tooltip
            formatter={(value, name) => [fmtWon(value), name]}
          />
          <Legend />
          <Bar
            dataKey="recognized_net_won"
            fill="#3182f6"
            name="확인 Net 매출"
            radius={[6, 6, 0, 0]}
          />
          <Bar
            dataKey="b2g_external_contracted_won"
            fill="#d1d6db"
            name="외부계약 추정"
            radius={[6, 6, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const OverviewTab = ({ report }) => {
  const headline = report.headline || {};
  const revenue = report.revenue || {};
  const coupon = report.coupon_lifecycle || {};
  const renewal = report.paid_renewal?.overall || {};
  const refund = report.refund_vs_activation || {};
  const activatedRefund = refund.activated_within_7d || {};
  const inactiveRefund = refund.not_activated_within_7d || {};

  const bottlenecks = [
    {
      title: '체험 가치가 결제 이유로 이어지지 않음',
      fact: `만료 체험 ${fmtNum(coupon.expired_free_or_marketing_users)}명 중 B2C 전환 ${fmtPct(coupon.expired_b2c_only_conversion_rate_pct)}`,
      impact: `${fmtNum(
        (coupon.expired_free_or_marketing_users || 0)
        - (coupon.expired_b2c_converted_users || 0)
      )}명이 아직 직접 결제로 이어지지 않았어요.`,
      next: '체험 중 첫 업무 완료 여부에 따라 전환 메시지를 나누는 실험',
    },
    {
      title: '기간제 이용권 갱신이 끊김',
      fact: `관찰 완료 ${fmtNum(renewal.matured_payments)}건 중 갱신 ${fmtPct(renewal.renewal_rate_pct)}`,
      impact: `미갱신률은 ${fmtPct(renewal.paid_churn_rate_pct)}예요.`,
      next: '만료 30·14·7·1일 전 가치 요약과 연장 동선 실험',
    },
    {
      title: '결제 직후 첫 성과가 환불과 연결됨',
      fact: `7일 내 활성 환불 ${fmtPct(activatedRefund.refund_rate_pct)} · 미활성 환불 ${fmtPct(inactiveRefund.refund_rate_pct)}`,
      impact: `미활성 결제 ${fmtNum(inactiveRefund.payments)}건이 우선 복구 대상이에요.`,
      next: '결제 후 24시간 내 결과가 없을 때 첫 업무 완료 플로우 제공',
    },
  ];

  return (
    <>
      <section className="revenue-funnel-kpi-grid" aria-label="핵심 지표">
        <KpiCard
          grade="A"
          label="확인된 누적 Net 매출"
          value={fmtWon(headline.recognized_net_revenue_won)}
          note="B2C·B2G 토스·연수 승인액에서 환불을 차감했어요."
        />
        <KpiCard
          grade="B"
          label="가입 후 30일 B2C 결제"
          value={fmtPct(headline.b2c_signup_30d_conversion_pct)}
          note="학교 좌석 활성화와 개인 직접 결제를 분리했어요."
        />
        <KpiCard
          grade="B"
          label="만료 체험 B2C 전환"
          value={fmtPct(headline.expired_trial_b2c_conversion_pct)}
          note="체험 만료자 중 이후 개인 결제한 비율이에요."
        />
        <KpiCard
          grade="A"
          label="만료 전후 30일 갱신"
          value={fmtPct(headline.paid_renewal_rate_pct)}
          note="자동구독 해지율이 아닌 기간제 재구매율이에요."
        />
      </section>

      <section className="revenue-funnel-section">
        <SectionHeader
          title="지금 먼저 확인할 병목"
          description="팩트와 다음 검증을 분리했어요. 예상 매출은 보장값으로 표시하지 않습니다."
        />
        <div className="revenue-funnel-bottlenecks">
          {bottlenecks.map((item, index) => (
            <article className="revenue-funnel-bottleneck" key={item.title}>
              <span className="revenue-funnel-rank">{index + 1}</span>
              <div>
                <h3>{item.title}</h3>
                <strong>{item.fact}</strong>
                <p>{item.impact}</p>
                <p className="revenue-funnel-next">다음 검증: {item.next}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="revenue-funnel-section">
        <SectionHeader
          title="월별 매출 구조"
          description="파랑은 승인·환불 원장 팩트, 회색은 관리자 쿠폰 기반 외부계약 추정입니다."
        />
        <RevenueTrendChart rows={revenue.monthly || []} />
      </section>
    </>
  );
};

export const EmailVerificationSection = () => {
  const [days, setDays] = useState(14);
  const [state, setState] = useState({ loading: true, data: null, error: '' });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: '' }));
    Promise.resolve()
      .then(() => getEmailVerificationSummary(days))
      .then((data) => {
        if (!cancelled) setState({ loading: false, data, error: '' });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, data: null, error: '불러오지 못했어요.' });
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  const data = state.data || {};
  return (
    <section className="revenue-funnel-section">
      <SectionHeader
        title="이메일 가입 → 인증 완료"
        description="Firebase 계정 기준이라 다른 기기에서 링크를 눌러도 잡혀요. Google 가입은 인증이 필요 없어 따로 셌어요."
      />
      <div className="revenue-funnel-segment" role="group" aria-label="기간">
        {[7, 14, 30].map((value) => (
          <button
            key={value}
            type="button"
            className={value === days ? 'is-active' : ''}
            onClick={() => setDays(value)}
          >
            최근 {value}일
          </button>
        ))}
      </div>
      {state.error ? (
        <p className="revenue-funnel-muted">{state.error}</p>
      ) : (
        <div className="revenue-funnel-window-grid">
          <article>
            <span>인증 완료율</span>
            <strong>{state.loading ? '…' : fmtPct(data.verification_rate_pct)}</strong>
            <p>{fmtNum(data.email_verified)} / {fmtNum(data.email_signups)}명</p>
          </article>
          <article>
            <span>하루 넘게 인증 안 함</span>
            <strong>{state.loading ? '…' : `${fmtNum(data.email_stuck_over_1d)}명`}</strong>
            <p>메일을 못 받았거나 주소를 잘못 쓴 가능성</p>
          </article>
          <article>
            <span>Google 가입</span>
            <strong>{state.loading ? '…' : `${fmtNum(data.google_signups)}명`}</strong>
            <p>전체 가입 {fmtNum(data.total_signups)}명 중</p>
          </article>
        </div>
      )}
    </section>
  );
};

const FunnelTab = ({ report }) => {
  const signup = report.signup_conversion?.b2c_self_serve || {};
  const allPaid = report.signup_conversion?.all_paid_entitlement || {};
  const funnel = report.funnel || {};
  const coupon = report.coupon_lifecycle || {};
  const acquisition = report.acquisition?.rows || [];
  const ga4 = report.ga4 || {};
  const maxStage = Math.max(
    1,
    ...(funnel.stages || []).map((row) => row.unique_users || 0)
  );

  return (
    <>
      <EmailVerificationSection />
      <section className="revenue-funnel-section">
        <SectionHeader
          title="가입 후 직접 결제"
          description="관찰창이 닫힌 가입자만 분모에 넣었습니다."
        />
        <div className="revenue-funnel-window-grid">
          {['7d', '14d', '30d'].map((window) => {
            const row = signup.totals?.[window] || {};
            return (
              <article key={window}>
                <span>가입 후 {window.replace('d', '일')}</span>
                <strong>{fmtPct(row.conversion_rate_pct)}</strong>
                <p>{fmtNum(row.converted)} / {fmtNum(row.eligible)}명</p>
              </article>
            );
          })}
        </div>
        <div className="revenue-funnel-inline-note">
          B2G 유료 좌석까지 포함하면 30일 전환은{' '}
          <strong>{fmtPct(allPaid.totals?.['30d']?.conversion_rate_pct)}</strong>
          입니다. 개인 결제 의사결정과 섞어 해석하지 않습니다.
        </div>
      </section>

      <section className="revenue-funnel-section">
        <SectionHeader
          title="결제 관심에서 승인까지"
          description="각 단계 집합과 같은 기간 결제자의 교집합입니다. 완전한 순차 퍼널이 아닌 단계별 도달 신호예요."
        />
        <div className="revenue-funnel-stage-list">
          {(funnel.stages || []).map((row) => (
            <article key={row.event_type}>
              <div className="revenue-funnel-stage-label">
                <span>{FUNNEL_LABELS[row.event_type] || row.event_type}</span>
                <strong>{fmtNum(row.unique_users)}명</strong>
              </div>
              <div className="revenue-funnel-stage-track">
                <span
                  style={{
                    width: `${Math.max(
                      2,
                      ((row.unique_users || 0) / maxStage) * 100
                    )}%`,
                  }}
                />
              </div>
              <p>
                같은 기간 결제 {fmtNum(row.users_paid_in_period)}명 ·{' '}
                {fmtPct(row.user_to_paid_pct)}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="revenue-funnel-split">
        <article className="revenue-funnel-section">
          <SectionHeader title="무료체험 전환" />
          <dl className="revenue-funnel-definition-list">
            <div>
              <dt>체험 경험</dt>
              <dd>{fmtNum(coupon.free_or_marketing_users)}명</dd>
            </div>
            <div>
              <dt>만료 사용자</dt>
              <dd>{fmtNum(coupon.expired_free_or_marketing_users)}명</dd>
            </div>
            <div>
              <dt>만료 후 B2C 전환</dt>
              <dd>{fmtPct(coupon.expired_b2c_only_conversion_rate_pct)}</dd>
            </div>
          </dl>
        </article>

        <article className="revenue-funnel-section">
          <SectionHeader title="GA4 최근 유입" />
          {ga4.available ? (
            <dl className="revenue-funnel-definition-list">
              <div>
                <dt>활성 사용자</dt>
                <dd>{fmtNum(ga4.last_30d?.activeUsers)}</dd>
              </div>
              <div>
                <dt>세션</dt>
                <dd>{fmtNum(ga4.last_30d?.sessions)}</dd>
              </div>
              <div>
                <dt>이탈률</dt>
                <dd>{fmtPct((ga4.last_30d?.bounceRate || 0) * 100)}</dd>
              </div>
            </dl>
          ) : (
            <p className="revenue-funnel-empty">GA4 집계를 불러오지 못했어요.</p>
          )}
        </article>
      </section>

      <section className="revenue-funnel-section">
        <SectionHeader
          title="최초 유입별 30일 B2C 전환"
          description="UTM 미상 비중이 높으므로 예산 의사결정보다 계측 품질 개선에 먼저 사용합니다."
        />
        <div className="revenue-funnel-table-wrap">
          <table className="revenue-funnel-table">
            <thead>
              <tr>
                <th>유입</th>
                <th className="num">관찰 가입</th>
                <th className="num">B2C 결제</th>
                <th className="num">30일 전환</th>
                <th className="num">첫 결제 Net</th>
              </tr>
            </thead>
            <tbody>
              {acquisition.map((row) => (
                <tr key={row.channel}>
                  <td>{row.channel}</td>
                  <td className="num">{fmtNum(row.eligible_users)}</td>
                  <td className="num">{fmtNum(row.converted_users)}</td>
                  <td className="num">{fmtPct(row.conversion_30d_pct)}</td>
                  <td className="num">{fmtWon(row.b2c_net_won)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
};

const ProductTab = ({ report }) => {
  const usageRows = report.feature_usage?.rows || [];
  const retentionByFeature = new Map(
    (report.product_retention?.rows || []).map((row) => [row.feature, row])
  );
  const conversionByFeature = new Map(
    (report.feature_to_payment?.b2c_first_7d?.rows || [])
      .map((row) => [row.feature, row])
  );

  return (
    <section className="revenue-funnel-section">
      <SectionHeader
        title="기능 가치와 결제 신호"
        description="완료·반복·다음 달 재사용은 효용 근거이고, 결제 차이는 인과가 아닌 연관입니다."
      />
      <div className="revenue-funnel-table-wrap">
        <table className="revenue-funnel-table">
          <thead>
            <tr>
              <th>기능</th>
              <th className="num">사용자</th>
              <th className="num">반복률</th>
              <th className="num">M1</th>
              <th className="num">7일 경험 결제율</th>
              <th className="num">보정 차이</th>
            </tr>
          </thead>
          <tbody>
            {usageRows.map((row) => {
              const retention = retentionByFeature.get(row.feature) || {};
              const conversion = conversionByFeature.get(row.feature) || {};
              const standardized = conversion.standardized || {};
              return (
                <tr key={row.feature}>
                  <td>
                    <strong>{row.label}</strong>
                    <small>{fmtNum(row.units)} 처리 단위</small>
                  </td>
                  <td className="num">{fmtNum(row.unique_users)}</td>
                  <td className="num">{fmtPct(row.repeat_user_rate_pct)}</td>
                  <td className="num">{fmtPct(retention.m1_retention_pct)}</td>
                  <td className="num">
                    {fmtPct(conversion.exposed_conversion_rate_pct)}
                  </td>
                  <td className="num">
                    {standardized.available
                      ? `${standardized.lift_pp > 0 ? '+' : ''}${standardized.lift_pp}pp`
                      : '표본 부족'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="revenue-funnel-method-note">
        채점은 전환 후보, 생기부는 반복·리텐션 후보로 관찰됩니다. 대형 변경 전에
        기능별 A/B 테스트가 필요합니다.
      </p>
    </section>
  );
};

const RevenueTab = ({ report }) => {
  const revenue = report.revenue || {};
  const lifetime = revenue.lifetime || {};

  return (
    <>
      <section className="revenue-funnel-kpi-grid">
        <KpiCard
          grade="A"
          label="B2C Net"
          value={fmtWon(lifetime.b2c_net_won)}
          note={`환불 ${fmtWon(lifetime.b2c_refunded_won)} 차감`}
        />
        <KpiCard
          grade="A"
          label="B2G 토스 Net"
          value={fmtWon(lifetime.b2g_toss_net_won)}
          note="단체구매 결제 원장 기준"
        />
        <KpiCard
          grade="C"
          label="외부계약 추정"
          value={fmtWon(lifetime.b2g_external_contracted_won)}
          note="현금 입금이 아닌 관리자 유료 쿠폰 추정"
        />
        <KpiCard
          grade="A"
          label="확인 Net 합계"
          value={fmtWon(lifetime.recognized_net_won)}
          note="추정 계약액을 합치지 않은 값"
        />
      </section>

      <section className="revenue-funnel-section">
        <SectionHeader title="월별 매출" />
        <RevenueTrendChart rows={revenue.monthly || []} />
        <div className="revenue-funnel-table-wrap">
          <table className="revenue-funnel-table">
            <thead>
              <tr>
                <th>월</th>
                <th className="num">B2C Net</th>
                <th className="num">B2G 토스 Net</th>
                <th className="num">확인 Net</th>
                <th className="num">외부계약 추정</th>
              </tr>
            </thead>
            <tbody>
              {(revenue.monthly || []).slice(-12).map((row) => (
                <tr key={row.month}>
                  <td>{row.month}</td>
                  <td className="num">{fmtWon(row.b2c_net_won)}</td>
                  <td className="num">{fmtWon(row.b2g_toss_net_won)}</td>
                  <td className="num emphasis">
                    {fmtWon(row.recognized_net_won)}
                  </td>
                  <td className="num">
                    {fmtWon(row.b2g_external_contracted_won)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="revenue-funnel-section">
        <SectionHeader
          title="플랜별 매출"
          description="전환율이 아니라 방문자당 Net 매출과 환불률을 함께 봅니다."
        />
        <div className="revenue-funnel-table-wrap">
          <table className="revenue-funnel-table">
            <thead>
              <tr>
                <th>플랜</th>
                <th className="num">결제</th>
                <th className="num">고유 결제자</th>
                <th className="num">Net</th>
                <th className="num">환불 건 비율</th>
              </tr>
            </thead>
            <tbody>
              {(revenue.plan_mix || []).map((row) => (
                <tr key={row.segment}>
                  <td>{row.segment}</td>
                  <td className="num">{fmtNum(row.payments)}</td>
                  <td className="num">{fmtNum(row.unique_payers)}</td>
                  <td className="num">{fmtWon(row.net_won)}</td>
                  <td className="num">{fmtPct(row.refund_rate_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
};

const RetentionTab = ({ report }) => {
  const renewal = report.paid_renewal || {};
  const refund = report.refund_vs_activation || {};
  const productRows = report.product_retention?.rows || [];

  return (
    <>
      <section className="revenue-funnel-split">
        <article className="revenue-funnel-section">
          <SectionHeader title="만료 코호트 갱신" />
          <div className="revenue-funnel-big-number">
            {fmtPct(renewal.overall?.renewal_rate_pct)}
          </div>
          <p>
            {fmtNum(renewal.overall?.renewed_payments)} /{' '}
            {fmtNum(renewal.overall?.matured_payments)}건
          </p>
          <p className="revenue-funnel-method-note">
            만료 예상일 전후 30일 내 다음 B2C 결제를 갱신으로 봅니다.
          </p>
        </article>

        <article className="revenue-funnel-section">
          <SectionHeader title="결제 후 활성화와 환불" />
          <dl className="revenue-funnel-definition-list">
            <div>
              <dt>7일 내 핵심 완료</dt>
              <dd>
                {fmtPct(refund.activated_within_7d?.refund_rate_pct)}
              </dd>
            </div>
            <div>
              <dt>7일 내 완료 없음</dt>
              <dd>
                {fmtPct(refund.not_activated_within_7d?.refund_rate_pct)}
              </dd>
            </div>
          </dl>
        </article>
      </section>

      <section className="revenue-funnel-section">
        <SectionHeader
          title="기능별 M1·M2 리텐션"
          description="첫 사용월 이후 같은 기능을 다시 완료한 사용자 기준입니다."
        />
        <div className="revenue-funnel-table-wrap">
          <table className="revenue-funnel-table">
            <thead>
              <tr>
                <th>기능</th>
                <th className="num">첫 사용</th>
                <th className="num">M1 대상</th>
                <th className="num">M1</th>
                <th className="num">M2 대상</th>
                <th className="num">M2</th>
              </tr>
            </thead>
            <tbody>
              {productRows.map((row) => (
                <tr key={row.feature}>
                  <td>{row.label}</td>
                  <td className="num">{fmtNum(row.first_use_users)}</td>
                  <td className="num">{fmtNum(row.m1_eligible)}</td>
                  <td className="num">{fmtPct(row.m1_retention_pct)}</td>
                  <td className="num">{fmtNum(row.m2_eligible)}</td>
                  <td className="num">{fmtPct(row.m2_retention_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
};

const QualityTab = ({ report }) => {
  const quality = report.data_quality || {};
  const counts = quality.collection_document_counts || {};
  const definitions = report.definitions || {};

  return (
    <>
      <section className="revenue-funnel-section">
        <SectionHeader
          title="데이터 신뢰도"
          description="숫자가 없는 영역은 추정으로 채우지 않습니다."
        />
        <div className="revenue-funnel-quality-grid">
          <article>
            <strong>A</strong>
            <h3>원장 팩트</h3>
            <p>승인·환불·완료 문서에서 직접 계산한 값</p>
          </article>
          <article>
            <strong>B</strong>
            <h3>강한 행동 근거</h3>
            <p>시간 순서를 지킨 사용자 행동의 연관</p>
          </article>
          <article>
            <strong>C</strong>
            <h3>추정</h3>
            <p>계약 메타나 가격 규칙으로 환산한 값</p>
          </article>
        </div>
      </section>

      <section className="revenue-funnel-section">
        <SectionHeader title="현재 알려진 공백" />
        <ul className="revenue-funnel-gap-list">
          {(quality.known_gaps || []).map((gap) => (
            <li key={gap}>{gap}</li>
          ))}
        </ul>
        {(quality.warnings || []).length > 0 && (
          <div className="revenue-funnel-warning">
            일부 원장을 읽지 못한 경고가 {fmtNum(quality.warnings.length)}건 있어요.
          </div>
        )}
      </section>

      <section className="revenue-funnel-split">
        <article className="revenue-funnel-section">
          <SectionHeader title="읽은 문서 수" />
          <dl className="revenue-funnel-count-list">
            {Object.entries(counts).map(([collection, count]) => (
              <div key={collection}>
                <dt>{collection}</dt>
                <dd>{fmtNum(count)}</dd>
              </div>
            ))}
          </dl>
        </article>

        <article className="revenue-funnel-section">
          <SectionHeader title="핵심 정의" />
          <dl className="revenue-funnel-count-list">
            {Object.entries(definitions).map(([key, value]) => (
              <div key={key}>
                <dt>{key}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </article>
      </section>
    </>
  );
};

const RevenueFunnelPage = () => {
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);
  const [permissions, setPermissions] = useState(null);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [permissionError, setPermissionError] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    let cancelled = false;
    const loadPermissions = async () => {
      if (!user) {
        setPermissionLoading(false);
        return;
      }
      setPermissionLoading(true);
      setPermissionError('');
      try {
        const result = await getAdminPermissions();
        if (!cancelled) setPermissions(result);
      } catch (permissionFailure) {
        if (!cancelled) {
          setPermissionError(
            permissionFailure.message || '권한을 확인하지 못했어요.'
          );
        }
      } finally {
        if (!cancelled) setPermissionLoading(false);
      }
    };
    loadPermissions();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const loadReport = useCallback(async (refresh = false) => {
    setLoading(true);
    setError('');
    try {
      const result = await getRevenueFunnelReport({
        refresh,
        includeGa4: true,
        includeLogs: false,
      });
      setReport(result);
    } catch (reportError) {
      setError(reportError.message || '매출·퍼널 지표를 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (permissions?.is_super_admin && !report && !loading && !error) {
      loadReport(false);
    }
  }, [error, loadReport, loading, permissions, report]);

  const content = useMemo(() => {
    if (!report) return null;
    if (activeTab === 'funnel') return <FunnelTab report={report} />;
    if (activeTab === 'product') return <ProductTab report={report} />;
    if (activeTab === 'revenue') return <RevenueTab report={report} />;
    if (activeTab === 'retention') return <RetentionTab report={report} />;
    if (activeTab === 'quality') return <QualityTab report={report} />;
    return <OverviewTab report={report} />;
  }, [activeTab, report]);

  if (authLoading || permissionLoading) return <PageSkeleton />;

  if (!user) {
    return (
      <AccessState
        title="로그인이 필요해요"
        description="슈퍼 관리자 계정으로 로그인해주세요."
        actionLabel="로그인하기"
        onAction={() => navigate('/auth?redirect=/admin/revenue-funnel')}
      />
    );
  }

  if (permissionError) {
    return (
      <AccessState
        title="권한을 확인하지 못했어요"
        description={permissionError}
        actionLabel="관리자 홈"
        onAction={() => navigate('/admin')}
      />
    );
  }

  if (!permissions?.is_super_admin) {
    return (
      <AccessState
        title="슈퍼 관리자만 사용할 수 있어요"
        description="결제·매출 지표 접근 권한이 있는 계정으로 확인해주세요."
        onAction={() => navigate('/admin')}
      />
    );
  }

  if (loading && !report) return <PageSkeleton />;

  return (
    <div className="revenue-funnel-page">
      <Helmet>
        <title>매출·퍼널 진단 | 왓퀴즈 Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <main className="revenue-funnel-container">
        <header className="revenue-funnel-header">
          <div>
            <button
              type="button"
              className="revenue-funnel-back"
              onClick={() => navigate('/admin')}
            >
              관리자 홈
            </button>
            <p className="revenue-funnel-eyebrow">WhatQuiz Admin</p>
            <h1>매출·퍼널 진단</h1>
            <p>
              어디에서 매출이 새고 있는지 원장 팩트와 행동 근거로 확인합니다.
            </p>
            {report?.meta?.as_of_utc && (
              <p className="revenue-funnel-generated">
                기준 {formatDateTime(report.meta.as_of_utc)}
                {' · '}
                {reportSourceLabel(report.service_meta)}
              </p>
            )}
          </div>
          <button
            type="button"
            className="revenue-funnel-refresh"
            disabled={loading}
            onClick={() => loadReport(true)}
          >
            {loading ? '계산 중' : '새로 계산하기'}
          </button>
        </header>

        <nav
          className="revenue-funnel-tabs"
          aria-label="매출 퍼널 분석 영역"
        >
          {TABS.map((tab) => (
            <button
              type="button"
              key={tab.key}
              className={activeTab === tab.key ? 'active' : ''}
              aria-current={activeTab === tab.key ? 'page' : undefined}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {error && (
          <section className="revenue-funnel-error" role="alert">
            <h2>지표를 불러오지 못했어요</h2>
            <p>{error}</p>
            <button type="button" onClick={() => loadReport(false)}>
              다시 불러오기
            </button>
          </section>
        )}
        {report && (
          <div className={loading ? 'revenue-funnel-content is-refreshing' : 'revenue-funnel-content'}>
            {content}
          </div>
        )}
      </main>
    </div>
  );
};

export default RevenueFunnelPage;
